/**
 * useFlagDismissals — shared hook for the summarization-agent flag
 * dismiss/restore feature.
 *
 * Wraps the `flags.dismissals` Convex query/mutations and exposes a
 * small predicate-and-action surface the four flag-bearing agent panels
 * (Asset List, Security Interests, Board Reports, ATO/SRO) consume.
 *
 * It also tracks which flag's reason editor is currently open
 * (`editingKey`) so the editor can be rendered separately from — and
 * positioned below — the flag it belongs to.
 */

import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { flagKey, type FlagAgentKind } from "./flagKey";

export interface FlagDismissal {
  _id: Id<"flagDismissals">;
  flagKey: string;
  flagType: string;
  flagLabel: string;
  entryLabel: string;
  entryId: string;
  reason?: string;
  dismissedAt: number;
  dismissedByName?: string;
}

/** A flag as the panels know it — just enough to derive its key. */
export interface FlagRef {
  type: string;
  /** Detail text feeding the key (reason / detail / details join / flagDetails). */
  detail: string;
}

export interface UseFlagDismissalsResult {
  /** Whether the current user may dismiss/restore flags. */
  canManage: boolean;
  /** True if this exact flag is currently dismissed. */
  isDismissed: (
    agentKind: FlagAgentKind,
    entryId: string,
    flag: FlagRef,
  ) => boolean;
  /** The dismissal record for this flag, if dismissed. */
  getDismissal: (
    agentKind: FlagAgentKind,
    entryId: string,
    flag: FlagRef,
  ) => FlagDismissal | undefined;
  /** Count of flags on a row that are NOT dismissed — drives the row glow. */
  activeCount: (
    agentKind: FlagAgentKind,
    entryId: string,
    flags: FlagRef[],
  ) => number;
  /** Dismiss a flag. */
  dismiss: (input: {
    agentKind: FlagAgentKind;
    entryId: string;
    entryLabel: string;
    flagType: string;
    flagLabel: string;
    flagDetail: string;
    reason?: string;
  }) => Promise<unknown>;
  /** Restore a previously dismissed flag. */
  restore: (input: {
    agentKind: FlagAgentKind;
    entryId: string;
    flag: FlagRef;
  }) => Promise<unknown>;
  isPending: boolean;
  /** Stable key of the flag whose reason editor is open, or null. */
  editingKey: string | null;
  /** Open the reason editor for the given flag key. */
  startEditing: (key: string) => void;
  /** Close the reason editor. */
  stopEditing: () => void;
}

export function useFlagDismissals(jobId: Id<"jobs">): UseFlagDismissalsResult {
  const { data: dismissals } = useQuery(
    convexQuery(api.flags.dismissals.listForJob, { jobId }),
  );
  const { data: canManage } = useQuery(
    convexQuery(api.flags.dismissals.canManageFlags, { jobId }),
  );

  const dismissMut = useMutation({
    mutationFn: useConvexMutation(api.flags.dismissals.dismissFlag),
  });
  const restoreMut = useMutation({
    mutationFn: useConvexMutation(api.flags.dismissals.restoreFlag),
  });

  const [editingKey, setEditingKey] = useState<string | null>(null);

  return useMemo(() => {
    const byKey = new Map<string, FlagDismissal>();
    for (const d of dismissals ?? []) {
      byKey.set(d.flagKey, d);
    }

    const getDismissal = (
      agentKind: FlagAgentKind,
      entryId: string,
      flag: FlagRef,
    ) => byKey.get(flagKey(agentKind, entryId, flag.type, flag.detail));

    return {
      canManage: canManage ?? false,
      getDismissal,
      isDismissed: (agentKind, entryId, flag) =>
        getDismissal(agentKind, entryId, flag) !== undefined,
      activeCount: (agentKind, entryId, flags) =>
        flags.filter((f) => getDismissal(agentKind, entryId, f) === undefined)
          .length,
      dismiss: (input) =>
        dismissMut.mutateAsync({
          jobId,
          agentKind: input.agentKind,
          entryId: input.entryId,
          entryLabel: input.entryLabel,
          flagType: input.flagType,
          flagLabel: input.flagLabel,
          flagDetail: input.flagDetail,
          reason: input.reason,
        }),
      restore: (input) =>
        restoreMut.mutateAsync({
          jobId,
          entryId: input.entryId,
          flagKey: flagKey(
            input.agentKind,
            input.entryId,
            input.flag.type,
            input.flag.detail,
          ),
        }),
      isPending: dismissMut.isPending || restoreMut.isPending,
      editingKey,
      startEditing: (key: string) => setEditingKey(key),
      stopEditing: () => setEditingKey(null),
    };
  }, [dismissals, canManage, dismissMut, restoreMut, jobId, editingKey]);
}
